import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

export type Countdown = {
  months: number
  days: number
  hours: number
  minutes: number
}

const MINUTE = 60 * 1000

const daysInMonth = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()

/**
 * Adds whole months to a date, clamping the day of the month so that
 * e.g. January 31st + 1 month lands on February 28th instead of March 3rd.
 */
const addMonths = (date: Date, amount: number): Date => {
  const result = new Date(date.getTime())
  const day = result.getDate()

  result.setDate(1)
  result.setMonth(result.getMonth() + amount)
  result.setDate(Math.min(day, daysInMonth(result)))

  return result
}

const calculate = (target: Date, from: Date): Countdown => {
  if (target.getTime() <= from.getTime()) {
    return { months: 0, days: 0, hours: 0, minutes: 0 }
  }

  let months =
    (target.getFullYear() - from.getFullYear()) * 12 + (target.getMonth() - from.getMonth())

  // Stepping forward by calendar months can overshoot the target; step back one if it does.
  if (addMonths(from, months).getTime() > target.getTime()) {
    months--
  }

  const remainder = target.getTime() - addMonths(from, months).getTime()

  return {
    months,
    days: Math.floor(remainder / (24 * 60 * MINUTE)),
    hours: Math.floor(remainder / (60 * MINUTE)) % 24,
    minutes: Math.floor(remainder / MINUTE) % 60,
  }
}

/**
 * Counts down to `target` in calendar months, days, hours and minutes.
 *
 * Stays `null` until mounted, so server-rendered markup and the first client
 * render match. Ticks every minute and cleans up its interval on unmount.
 */
export const useCountdown = (target: Date, intervalMs: number = MINUTE): Ref<Countdown | null> => {
  const remaining = ref<Countdown | null>(null)

  let timer: ReturnType<typeof setInterval> | undefined

  onMounted(() => {
    const tick = () => {
      remaining.value = calculate(target, new Date())
    }

    tick()
    timer = setInterval(tick, intervalMs)
  })

  onBeforeUnmount(() => {
    if (timer) clearInterval(timer)
  })

  return remaining
}
